import React, { useState, useEffect, useRef } from 'react';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';
import Modal from '../components/Modal';

const Profile = () => {
  const { user, logout, updateProfile } = useAuth();
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showNameModal, setShowNameModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  
  // Form states
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Load saved profile from localStorage
  const savedProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
  
  // Address states
  const [billingAddress, setBillingAddress] = useState(savedProfile.billingAddress || '');
  const [billingAddress2, setBillingAddress2] = useState(savedProfile.billingAddress2 || '');
  const [billingCity, setBillingCity] = useState(savedProfile.billingCity || '');
  const [billingState, setBillingState] = useState(savedProfile.billingState || '');
  const [billingZip, setBillingZip] = useState(savedProfile.billingZip || '');
  const [billingCountry, setBillingCountry] = useState(savedProfile.billingCountry || 'ZA');
  
  const [shippingAddress, setShippingAddress] = useState(savedProfile.shippingAddress || '');
  const [shippingAddress2, setShippingAddress2] = useState(savedProfile.shippingAddress2 || '');
  const [shippingCity, setShippingCity] = useState(savedProfile.shippingCity || '');
  const [shippingState, setShippingState] = useState(savedProfile.shippingState || '');
  const [shippingZip, setShippingZip] = useState(savedProfile.shippingZip || '');
  const [shippingCountry, setShippingCountry] = useState(savedProfile.shippingCountry || 'ZA');
  const [sameAsBilling, setSameAsBilling] = useState(savedProfile.sameAsBilling ?? true);

  // Refs for autocomplete
  const billingInputRef = useRef(null);
  const shippingInputRef = useRef(null);
  const autocompleteBilling = useRef(null);
  const autocompleteShipping = useRef(null);

  // Load Google Maps script
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBvYq6yNASqpfBkXWuUUliHe5dJmg5mlJs&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Google Maps loaded in Profile');
      };
      document.head.appendChild(script);
    }
  }, []);

  // Initialize autocomplete when modal opens
  useEffect(() => {
    if (showAddressModal) {
      if (window.google && window.google.maps && window.google.maps.places) {
        // Clear previous instances
        if (autocompleteBilling.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteBilling.current);
        }
        if (autocompleteShipping.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteShipping.current);
        }
        autocompleteBilling.current = null;
        autocompleteShipping.current = null;
        setTimeout(initializeAutocomplete, 300);
      } else {
        // Google Maps not loaded yet, wait and try again
        setTimeout(initializeAutocomplete, 500);
      }
    }
  }, [showAddressModal, sameAsBilling]);

  const initializeAutocomplete = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps not loaded yet, retrying...');
      setTimeout(initializeAutocomplete, 500);
      return;
    }

    console.log('Initializing autocomplete...');

    // Initialize billing address autocomplete
    if (billingInputRef.current && !autocompleteBilling.current) {
      try {        
        autocompleteBilling.current = new window.google.maps.places.Autocomplete(billingInputRef.current, {
          types: ['address'],
          fields: ['address_components', 'formatted_address', 'geometry']
        });
        
        autocompleteBilling.current.addListener('place_changed', () => {
          const place = autocompleteBilling.current.getPlace();
          console.log('Billing place selected:', place);
          
          if (!place.geometry) {
            console.log('No details available for input: ' + place.name);
            return;
          }
          
          // Parse and set address components
          if (place.address_components) {
            parseAddressComponents(place.address_components, 'billing');
          }
        });
        console.log('✓ Billing autocomplete initialized');
      } catch (error) {
        console.error('Error initializing billing autocomplete:', error);
      }
    }

    // Initialize shipping address autocomplete
    if (shippingInputRef.current && !sameAsBilling && !autocompleteShipping.current) {
      try {        
        autocompleteShipping.current = new window.google.maps.places.Autocomplete(shippingInputRef.current, {
          types: ['address'],
          fields: ['address_components', 'formatted_address', 'geometry']
        });
        
        autocompleteShipping.current.addListener('place_changed', () => {
          const place = autocompleteShipping.current.getPlace();
          console.log('Shipping place selected:', place);
          
          if (!place.geometry) {
            console.log('No details available for input: ' + place.name);
            return;
          }
          
          // Parse and set address components
          if (place.address_components) {
            parseAddressComponents(place.address_components, 'shipping');
          }
        });
        console.log('✓ Shipping autocomplete initialized');
      } catch (error) {
        console.error('Error initializing shipping autocomplete:', error);
      }
    }
  };

  const parseAddressComponents = (components, type) => {
    let street = '';
    let city = '';
    let state = '';
    let zip = '';
    let country = '';

    components.forEach(component => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        street = component.long_name + ' ';
      }
      if (types.includes('route')) {
        street += component.long_name;
      }
      if (types.includes('locality')) {
        city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.long_name;
      }
      if (types.includes('postal_code')) {
        zip = component.long_name;
      }
      if (types.includes('country')) {
        country = component.short_name;
      }
    });

    if (type === 'billing') {
      if (street) setBillingAddress(street);
      if (city) setBillingCity(city);
      if (state) setBillingState(state);
      if (zip) setBillingZip(zip);
      if (country) setBillingCountry(country);
    } else {
      if (street) setShippingAddress(street);
      if (city) setShippingCity(city);
      if (state) setShippingState(state);
      if (zip) setShippingZip(zip);
      if (country) setShippingCountry(country);
    }
  };

  const showSuccessMessage = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const showErrorMessage = (message) => {
    setError(message);
    setTimeout(() => setError(''), 3000);
  };

  const handleUpload = async (e) => {
    if (e.target && e.target.files) {
      const file = e.target.files[0];
      if (file) {
        setLoading(true);
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (!authUser) throw new Error('Not authenticated');

          // Delete old profile picture if exists
          if (user?.profilePicture && user.profilePicture.includes('supabase')) {
            const oldPath = user.profilePicture.split('/').pop();
            await supabase.storage
              .from('profile-pictures')
              .remove([`${authUser.id}/${oldPath}`]);
          }

          // Upload new profile picture
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}.${fileExt}`;
          const filePath = `${authUser.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('profile-pictures')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('profile-pictures')
            .getPublicUrl(filePath);

          // Update database
          const { error: updateError } = await supabase
            .from('users')
            .update({ profile_picture: publicUrl })
            .eq('id', authUser.id);

          if (updateError) throw updateError;

          await updateProfile({ profilePicture: publicUrl });
          showSuccessMessage('Profile picture updated successfully!');
        } catch (err) {
          console.error('Error uploading profile picture:', err);
          showErrorMessage('Failed to upload profile picture. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showErrorMessage('Name cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('users')
        .update({ full_name: name.trim() })
        .eq('id', authUser.id);

      if (error) throw error;

      await updateProfile({ name: name.trim() });
      setShowNameModal(false);
      showSuccessMessage('Name updated successfully!');
    } catch (err) {
      console.error('Error updating name:', err);
      showErrorMessage('Failed to update name. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      showErrorMessage('Phone number cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('users')
        .update({ phone: phone.trim() })
        .eq('id', authUser.id);

      if (error) throw error;

      await updateProfile({ phone: phone.trim() });
      setShowPhoneModal(false);
      showSuccessMessage('Phone number updated successfully!');
    } catch (err) {
      console.error('Error updating phone:', err);
      showErrorMessage('Failed to update phone number. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      showErrorMessage('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      showErrorMessage('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      showErrorMessage('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showSuccessMessage('Password updated successfully!');
    } catch (err) {
      console.error('Error updating password:', err);
      showErrorMessage(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      // Update database
      const { error: dbError } = await supabase
        .from('users')
        .update({
          billing_address: billingAddress,
          billing_address2: billingAddress2,
          billing_city: billingCity,
          billing_state: billingState,
          billing_zip: billingZip,
          billing_country: billingCountry,
          shipping_address: sameAsBilling ? billingAddress : shippingAddress,
          shipping_address2: sameAsBilling ? billingAddress2 : shippingAddress2,
          shipping_city: sameAsBilling ? billingCity : shippingCity,
          shipping_state: sameAsBilling ? billingState : shippingState,
          shipping_zip: sameAsBilling ? billingZip : shippingZip,
          shipping_country: sameAsBilling ? billingCountry : shippingCountry,
          shipping_same_as_billing: sameAsBilling
        })
        .eq('id', authUser.id);

      if (dbError) throw dbError;

      // Also save to localStorage for quick access
      const profileData = {
        firstName: name.split(' ')[0] || '',
        lastName: name.split(' ').slice(1).join(' ') || '',
        email: user?.email,
        phone: user?.phone,
        billingAddress,
        billingAddress2,
        billingCity,
        billingState,
        billingZip,
        billingCountry,
        shippingAddress: sameAsBilling ? billingAddress : shippingAddress,
        shippingAddress2: sameAsBilling ? billingAddress2 : shippingAddress2,
        shippingCity: sameAsBilling ? billingCity : shippingCity,
        shippingState: sameAsBilling ? billingState : shippingState,
        shippingZip: sameAsBilling ? billingZip : shippingZip,
        shippingCountry: sameAsBilling ? billingCountry : shippingCountry,
        sameAsBilling
      };

      localStorage.setItem('userProfile', JSON.stringify(profileData));
      setShowAddressModal(false);
      showSuccessMessage('Address saved to database successfully!');
    } catch (err) {
      console.error('Error updating address:', err);
      showErrorMessage('Failed to save address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-wrapper">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div style={{
            position: 'relative',
            width: '150px',
            height: '150px',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '4px solid #CDB688',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: user?.profilePicture ? 'transparent' : 'linear-gradient(135deg, #f5f5f5, #e0e0e0)'
            }}>
              {user?.profilePicture ? (
                <img 
                  src={user.profilePicture} 
                  alt="Profile" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <i className="fas fa-user" style={{
                  fontSize: '4rem',
                  color: '#999'
                }}></i>
              )}
            </div>
            
            <label htmlFor="file-upload" style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              width: '45px',
              height: '45px',
              background: loading ? '#999' : '#CDB688',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s ease',
              pointerEvents: loading ? 'none' : 'auto'
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'scale(1.1)')}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {loading ? (
                <i className="fas fa-spinner fa-spin" style={{
                  color: 'white',
                  fontSize: '1.2rem'
                }}></i>
              ) : (
                <i className="fas fa-camera" style={{
                  color: 'white',
                  fontSize: '1.2rem'
                }}></i>
              )}
            </label>
            <input
              id="file-upload"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleUpload}
              style={{display: 'none'}}
              disabled={loading}
            />
          </div>
          
          <h2 style={{
            fontSize: '1.2rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.25rem'
          }}>Update Profile Picture</h2>
          <p style={{
            fontSize: '0.85rem',
            color: '#666',
            textAlign: 'center'
          }}>Click the camera icon to upload a new photo</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="member-info mb-xl">
          <p>Membership Number: {user?.email}</p>
          <p>Membership Level: {user?.membershipLevel}</p>
        </div>

        <div className="settings-section">
          <h3 className="section-title">Account Settings</h3>

          <div className="setting-item" onClick={() => {
            setName(user?.name || '');
            setShowNameModal(true);
          }} style={{ cursor: 'pointer' }}>
            <div>
              <div className="setting-label">Your name</div>
              <div className="setting-value">{user?.name || 'Not set'}</div>
            </div>
            <span className="edit-link">Edit</span>
          </div>

          <div className="setting-item">
            <div>
              <div className="setting-label">Your email</div>
              <div className="setting-value">{user?.email}</div>
            </div>
            <span style={{ opacity: 0.5, fontSize: '0.9rem' }}>Cannot be changed</span>
          </div>

          <div className="setting-item" onClick={() => {
            setPhone(user?.phone || '');
            setShowPhoneModal(true);
          }} style={{ cursor: 'pointer' }}>
            <div>
              <div className="setting-label">Your phone number</div>
              <div className="setting-value">{user?.phone || 'Not set'}</div>
            </div>
            <span className="edit-link">Edit</span>
          </div>

          <div className="setting-item" onClick={() => {
            const saved = JSON.parse(localStorage.getItem('userProfile') || '{}');
            setBillingAddress(saved.billingAddress || '');
            setBillingAddress2(saved.billingAddress2 || '');
            setBillingCity(saved.billingCity || '');
            setBillingState(saved.billingState || '');
            setBillingZip(saved.billingZip || '');
            setBillingCountry(saved.billingCountry || 'ZA');
            setShippingAddress(saved.shippingAddress || '');
            setShippingAddress2(saved.shippingAddress2 || '');
            setShippingCity(saved.shippingCity || '');
            setShippingState(saved.shippingState || '');
            setShippingZip(saved.shippingZip || '');
            setShippingCountry(saved.shippingCountry || 'ZA');
            setSameAsBilling(saved.sameAsBilling ?? true);
            setShowAddressModal(true);
          }} style={{ cursor: 'pointer' }}>
            <div>
              <div className="setting-label">Billing & Shipping Address</div>
              <div className="setting-value">
                {savedProfile.billingAddress ? `${savedProfile.billingAddress}, ${savedProfile.billingCity || ''}` : 'Not set'}
              </div>
            </div>
            <span className="edit-link">Edit</span>
          </div>

          <div className="setting-item" onClick={() => {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordModal(true);
          }} style={{ cursor: 'pointer' }}>
            <div>
              <div className="setting-label">Password</div>
            </div>
            <span className="edit-link">Update</span>
          </div>

          <div className="setting-item" style={{ borderBottom: 'none' }}>
            <button className="btn btn-secondary w-full" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Name Edit Modal */}
      <Modal isOpen={showNameModal} onClose={() => setShowNameModal(false)} title="Edit Name">
        <form onSubmit={handleUpdateName}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500'
            }}>
              Full Name
            </label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              disabled={loading}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowNameModal(false)}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Phone Edit Modal */}
      <Modal isOpen={showPhoneModal} onClose={() => setShowPhoneModal(false)} title="Edit Phone Number">
        <form onSubmit={handleUpdatePhone}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500'
            }}>
              Phone Number
            </label>
            <input
              type="tel"
              className="input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number"
              disabled={loading}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowPhoneModal(false)}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Password Update Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title="Update Password">
        <form onSubmit={handleUpdatePassword}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500'
            }}>
              New Password
            </label>
            <input
              type="password"
              className="input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={loading}
              required
              minLength={6}
            />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500'
            }}>
              Confirm New Password
            </label>
            <input
              type="password"
              className="input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={loading}
              required
              minLength={6}
            />
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowPasswordModal(false)}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Address Edit Modal */}
      <Modal isOpen={showAddressModal} onClose={() => setShowAddressModal(false)} title="Edit Address">
        <form onSubmit={handleUpdateAddress} style={{
          maxHeight: window.innerWidth < 600 ? '75vh' : '70vh', 
          overflowY: 'auto',
          padding: window.innerWidth < 400 ? '0.5rem' : '0'
        }}>
          {/* Billing Address Section */}
          <h3 style={{
            marginBottom: '1rem', 
            color: '#CDB688', 
            fontSize: window.innerWidth < 400 ? '1rem' : '1.1rem'
          }}>
            <i className="fas fa-file-invoice"></i> Billing Address
          </h3>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500',
              fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
            }}>
              Address Line 1 * <span style={{color: '#999', fontSize: window.innerWidth > 600 ? '0.8rem' : '0.75rem', fontWeight: 'normal', display: window.innerWidth < 400 ? 'block' : 'inline', marginTop: window.innerWidth < 400 ? '0.25rem' : '0'}}>(Start typing for suggestions)</span>
            </label>
            <input
              ref={billingInputRef}
              type="text"
              className="input"
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              placeholder="Start typing your address..."
              required
              autoComplete="off"
              style={{ fontSize: '16px' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#333',
              fontWeight: '500',
              fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
            }}>
              Address Line 2
            </label>
            <input
              type="text"
              className="input"
              value={billingAddress2}
              onChange={(e) => setBillingAddress2(e.target.value)}
              placeholder="Apartment, suite, etc."
              style={{ fontSize: '16px' }}
            />
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', 
            gap: '1rem', 
            marginBottom: '1rem' 
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#333',
                fontWeight: '500',
                fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
              }}>
                City *
              </label>
              <input
                type="text"
                className="input"
                value={billingCity}
                onChange={(e) => setBillingCity(e.target.value)}
                placeholder="City"
                required
                style={{ fontSize: '16px' }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#333',
                fontWeight: '500',
                fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
              }}>
                State/Province *
              </label>
              <input
                type="text"
                className="input"
                value={billingState}
                onChange={(e) => setBillingState(e.target.value)}
                placeholder="State"
                required
                style={{ fontSize: '16px' }}
              />
            </div>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', 
            gap: '1rem', 
            marginBottom: '1rem' 
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#333',
                fontWeight: '500',
                fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
              }}>
                ZIP/Postal Code *
              </label>
              <input
                type="text"
                className="input"
                value={billingZip}
                onChange={(e) => setBillingZip(e.target.value)}
                placeholder="Postal code"
                required
                style={{ fontSize: '16px' }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#333',
                fontWeight: '500',
                fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
              }}>
                Country *
              </label>
              <select
                className="input"
                value={billingCountry}
                onChange={(e) => setBillingCountry(e.target.value)}
                required
                style={{ fontSize: '16px' }}
              >
                <option value="ZA">South Africa</option>
                <option value="AE">United Arab Emirates</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="SA">Saudi Arabia</option>
                <option value="AU">Australia</option>
                <option value="CA">Canada</option>
              </select>
            </div>
          </div>

          {/* Same as Billing Checkbox */}
          <div style={{
            marginBottom: '1.5rem',
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#f9f9f9',
            borderRadius: '8px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '0.95rem'
            }}>
              <input
                type="checkbox"
                checked={sameAsBilling}
                onChange={(e) => setSameAsBilling(e.target.checked)}
                style={{ marginRight: '0.75rem', width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: '500' }}>Shipping address same as billing</span>
            </label>
          </div>

          {/* Shipping Address Section */}
          {!sameAsBilling && (
            <>
              <h3 style={{
                marginBottom: '1rem', 
                color: '#CDB688', 
                fontSize: window.innerWidth < 400 ? '1rem' : '1.1rem', 
                marginTop: '1.5rem'
              }}>
                <i className="fas fa-shipping-fast"></i> Shipping Address
              </h3>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#333',
                  fontWeight: '500',
                  fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                }}>
                  Address Line 1 * <span style={{color: '#999', fontSize: window.innerWidth > 600 ? '0.8rem' : '0.75rem', fontWeight: 'normal', display: window.innerWidth < 400 ? 'block' : 'inline', marginTop: window.innerWidth < 400 ? '0.25rem' : '0'}}>(Start typing for suggestions)</span>
                </label>
                <input
                  ref={shippingInputRef}
                  type="text"
                  className="input"
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder="Start typing your address..."
                  required={!sameAsBilling}
                  autoComplete="off"
                  style={{ fontSize: '16px' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#333',
                  fontWeight: '500',
                  fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                }}>
                  Address Line 2
                </label>
                <input
                  type="text"
                  className="input"
                  value={shippingAddress2}
                  onChange={(e) => setShippingAddress2(e.target.value)}
                  placeholder="Apartment, suite, etc."
                  style={{ fontSize: '16px' }}
                />
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', 
                gap: '1rem', 
                marginBottom: '1rem' 
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontWeight: '500',
                    fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                  }}>
                    City *
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={shippingCity}
                    onChange={(e) => setShippingCity(e.target.value)}
                    placeholder="City"
                    required={!sameAsBilling}
                    style={{ fontSize: '16px' }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontWeight: '500',
                    fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                  }}>
                    State/Province *
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={shippingState}
                    onChange={(e) => setShippingState(e.target.value)}
                    placeholder="State"
                    required={!sameAsBilling}
                    style={{ fontSize: '16px' }}
                  />
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', 
                gap: '1rem', 
                marginBottom: '1rem' 
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontWeight: '500',
                    fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                  }}>
                    ZIP/Postal Code *
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={shippingZip}
                    onChange={(e) => setShippingZip(e.target.value)}
                    placeholder="Postal code"
                    required={!sameAsBilling}
                    style={{ fontSize: '16px' }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontWeight: '500',
                    fontSize: window.innerWidth > 600 ? '0.9rem' : '0.85rem'
                  }}>
                    Country *
                  </label>
                  <select
                    className="input"
                    value={shippingCountry}
                    onChange={(e) => setShippingCountry(e.target.value)}
                    required={!sameAsBilling}
                    style={{ fontSize: '16px' }}
                  >
                    <option value="ZA">South Africa</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="US">United States</option>
                    <option value="GB">United Kingdom</option>
                    <option value="SA">Saudi Arabia</option>
                    <option value="AU">Australia</option>
                    <option value="CA">Canada</option>
                  </select>
                </div>
              </div>
            </>
          )}

          <div style={{ 
            display: 'flex', 
            flexDirection: window.innerWidth < 400 ? 'column' : 'row',
            gap: '1rem', 
            marginTop: '1.5rem' 
          }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => setShowAddressModal(false)}
              style={{ 
                flex: 1,
                minHeight: '44px',
                fontSize: window.innerWidth < 400 ? '0.95rem' : '1rem'
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              style={{ 
                flex: 1,
                minHeight: '44px',
                fontSize: window.innerWidth < 400 ? '0.95rem' : '1rem'
              }}
            >
              Save Address
            </button>
          </div>
        </form>
      </Modal>

      <BottomNav />
    </div>
  );
};

export default Profile;
